import React, { useEffect, useState } from 'react';
import { AlertTriangle, Plus, ShieldAlert } from 'lucide-react';
import Modal from './Modal';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';

const STATUS_LABEL = { OPEN: 'Open', ASSIGNED: 'Assigned', IN_PROGRESS: 'In progress', COMPLETED: 'Completed', ESCALATED: 'Escalated' };
const STATUS_BADGE = { OPEN: 'amber', ASSIGNED: 'purple', IN_PROGRESS: 'amber', COMPLETED: 'green', ESCALATED: 'red' };
const PRIORITY_BADGE = { routine: 'neutral', urgent: 'amber', critical: 'red' };
const TRIGGER_LABEL = { manual: 'Manual', phq9_item9_positive: 'PHQ-9 Item 9 (self-harm)' };
// Mirrors backend SAFETY_TRANSITIONS (OPEN→ASSIGNED→IN_PROGRESS→COMPLETED/ESCALATED, terminal states).
const TRANSITIONS = { OPEN: ['IN_PROGRESS', 'COMPLETED', 'ESCALATED'], ASSIGNED: ['IN_PROGRESS', 'COMPLETED', 'ESCALATED'], IN_PROGRESS: ['COMPLETED', 'ESCALATED'], COMPLETED: [], ESCALATED: [] };
const ACTIVE_STATUSES = ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'ESCALATED'];

const fmtDT = v => v ? new Date(v).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

function demoAssessment(user, overrides = {}) {
  return {
    id: `demo-sa-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    patient_id: null, source_type: 'manual', source_id: null, trigger_kind: 'manual',
    status: 'OPEN', priority: 'routine', assigned_to: null, assignee_name: null,
    created_by: user.id, creator_name: user.name, created_at: new Date().toISOString(),
    completed_at: null, outcome: null, notes: null, ...overrides,
  };
}

// Full safety-assessment list for a patient (open + closed). Used by the manager modal.
async function loadAssessments(patient, setAssessments, notify) {
  if (DEMO) { setAssessments(patient.safety_assessments || []); return; }
  try { const d = await api(`/patients/${patient.id}/safety-assessments`); setAssessments(d.assessments || []); } catch (e) { notify(e.message); }
}

export function SafetyAlertsCard({ patient, user, notify, logDemo, updateDemoPatient, afterChange, onManage }) {
  const open = (patient.open_safety_assessments || (DEMO ? (patient.safety_assessments || []).filter(a => ACTIVE_STATUSES.includes(a.status)) : [])).filter(a => ACTIVE_STATUSES.includes(a.status));
  if (!hasCap(user, 'safety.view')) return null;
  if (!open.length && !hasCap(user, 'safety.manage')) return null;
  return (
    <section className="content-card section-space no-print">
      <div className="content-heading">
        <div><div className="eyebrow">SAFETY</div><h3>Safety follow-up</h3><p>Persistent patient-level safety follow-up. Never an automated risk or disposition determination.</p></div>
        {hasCap(user, 'safety.manage') && <button className="button primary" onClick={onManage}><ShieldAlert size={15}/>Open safety assessment</button>}
      </div>
      {!open.length && <p className="empty-copy">No open safety follow-up.</p>}
      {open.map(a => (
        <div className={`inline-alert ${a.status === 'ESCALATED' || (a.status === 'OPEN' && a.priority !== 'routine') ? 'danger' : ''}`} role="alert" key={a.id}>
          <AlertTriangle size={16}/>
          <div><strong>{STATUS_LABEL[a.status] || a.status} · {TRIGGER_LABEL[a.trigger_kind] || a.trigger_kind}</strong>
            <small>{a.priority} · opened {fmtDT(a.created_at)}{a.assignee_name ? ` · assigned to ${a.assignee_name}` : ''}</small>
            {a.notes ? <p className="safety-note">{a.notes}</p> : null}</div>
        </div>
      ))}
    </section>
  );
}

export default function SafetyAssessmentModal({ patient, user, notify, logDemo, updateDemoPatient, afterChange, close, highlightId }) {
  const [assessments, setAssessments] = useState(patient.safety_assessments || []);
  const [busy, setBusy] = useState(null);
  const canManage = hasCap(user, 'safety.manage');

  useEffect(() => { loadAssessments(patient, setAssessments, notify); }, [patient.id]);

  function persist(next, msg) {
    if (DEMO) {
      updateDemoPatient(patient.id, p => ({ safety_assessments: next }));
      setAssessments(next);
      logDemo('safety.updated', { patient_id: patient.id });
    }
    if (msg) afterChange(msg);
  }

  async function create() {
    if (!canManage) return;
    setBusy('create');
    try {
      if (DEMO) {
        const entry = demoAssessment(user, { patient_id: patient.id });
        persist([entry, ...assessments], 'Safety follow-up opened.');
      } else {
        await api(`/patients/${patient.id}/safety-assessments`, { method: 'POST', body: {} });
        const d = await api(`/patients/${patient.id}/safety-assessments`);
        setAssessments(d.assessments || []);
        afterChange('Safety follow-up opened.');
      }
    } catch (e) { notify(e.message); } finally { setBusy(null); }
  }

  async function assign(a) {
    setBusy(a.id);
    try {
      if (DEMO) {
        const next = assessments.map(x => x.id === a.id ? { ...x, assigned_to: user.id, assignee_name: user.name } : x);
        persist(next, 'Safety follow-up assigned to you.');
      } else {
        await api(`/safety-assessments/${a.id}/assign`, { method: 'POST', body: { assigned_to: user.id } });
        await loadAssessments(patient, setAssessments, notify);
        afterChange('Safety follow-up assigned to you.');
      }
    } catch (e) { notify(e.message); } finally { setBusy(null); }
  }

  async function transition(a, status) {
    let reason = '', outcome = '';
    if (status === 'ESCALATED') { reason = window.prompt('Reason for escalation (audited):') || ''; if (reason.trim().length < 3) return; }
    if (status === 'COMPLETED') { outcome = window.prompt('Disposition / outcome (clinician-entered, never automated):') || ''; }
    setBusy(a.id);
    try {
      if (DEMO) {
        const next = assessments.map(x => x.id === a.id ? { ...x, status, completed_at: status === 'COMPLETED' ? new Date().toISOString() : x.completed_at, outcome: outcome || x.outcome } : x);
        persist(next, `Safety follow-up marked ${STATUS_LABEL[status].toLowerCase()}.`);
      } else {
        const body = { status };
        if (outcome) body.outcome = outcome;
        if (reason) body.reason = reason;
        await api(`/safety-assessments/${a.id}/status`, { method: 'POST', body });
        await loadAssessments(patient, setAssessments, notify);
        afterChange(`Safety follow-up marked ${STATUS_LABEL[status].toLowerCase()}.`);
      }
    } catch (e) { notify(e.message); } finally { setBusy(null); }
  }

  return (
    <Modal title="Safety assessment" onClose={close} wide>
      <div className="content-heading" style={{ marginBottom: 12 }}>
        <div><p className="empty-copy" style={{ margin: 0 }}>The system records follow-up; it never infers suicidality, risk level, PHP eligibility, hospitalization, or ED disposition. Those remain clinician decisions.</p></div>
        {canManage && <button className="button primary" disabled={busy === 'create'} onClick={create}><Plus size={15}/>Open follow-up</button>}
      </div>

      {!assessments.length && <p className="empty-copy">No safety follow-up records for this patient.</p>}
      {assessments.map(a => (
        <div className="safety-row" key={a.id} style={{ borderLeft: a.status === 'ESCALATED' ? '3px solid #ac4c44' : a.status === 'COMPLETED' ? '3px solid #628469' : '3px solid #bc985c' }}>
          <div className="safety-row-head">
            <span className={`badge ${STATUS_BADGE[a.status] || 'neutral'}`}>{STATUS_LABEL[a.status] || a.status}</span>
            <span className={`badge ${PRIORITY_BADGE[a.priority] || 'neutral'}`}>{a.priority}</span>
            <span className="badge neutral">{TRIGGER_LABEL[a.trigger_kind] || a.trigger_kind}</span>
            <small>{fmtDT(a.created_at)} · by {a.creator_name || '—'}{a.assignee_name ? ` · assigned ${a.assignee_name}` : ''}</small>
          </div>
          {a.notes ? <p className="safety-note">{a.notes}</p> : null}
          {a.outcome ? <p className="safety-note"><strong>Disposition:</strong> {a.outcome}</p> : null}
          {canManage && ACTIVE_STATUSES.includes(a.status) && (
            <div className="safety-row-actions">
              {!a.assigned_to && <button className="text-button" disabled={busy === a.id} onClick={() => assign(a)}>Assign to me</button>}
              {(TRANSITIONS[a.status] || []).map(s => (
                <button key={s} className="text-button" disabled={busy === a.id} onClick={() => transition(a, s)}>
                  {s === 'IN_PROGRESS' ? 'Start work' : s === 'COMPLETED' ? 'Complete' : 'Escalate'}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </Modal>
  );
}
